import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import { LaunchRounded } from "@mui/icons-material";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import EditIcon from "@mui/icons-material/Edit";
import LaunchMailIcon from "@mui/icons-material/ForwardToInbox";
import { darken, IconButton, ListItem, ListItemText, SvgIcon, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { alpha, keyframes, lighten } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import gtag from "ga-gtag";
import { useSnackbar } from "notistack";

import constants from "../../assets/constants";
import { getClickableLinkPath, getCompletedLinkPath } from "../../assets/svgGraphics";
import * as AssessmentSession from "../../state/redux/assessmentSession";
import * as PartnerSession from "../../state/redux/partnerSession";
import * as PatientSession from "../../state/redux/patientSession";
import * as UserSession from "../../state/redux/userSession";
import { deepCopyObject } from "../../utils/helpers";
import AlertDialog from "../dialogs/alertDialog";
import AssessmentWizardDialog from "../dialogs/assessmentWizardDialog";
import SharedInvitationDialog from "../dialogs/sharedInvitationDialog";
import UserDialog from "../dialogs/userDialog";
import OmniAvatar from "../ui/omniAvatar";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const muiSvgIconHover = keyframes`
from {transform: scale(1) rotate(15deg);}
33% {transform: scale(1.10) rotate(17.5deg);}
66% {transform: scale(1) rotate(12.5deg);}
to {transform: rotate(15deg);}
`;

const PartnerTodo = props => {
  const { userODM, organizationODM, partnerInvites, userToDos, loadUserToDosRequest } = props;

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [showWelcomMessages] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showSharedInvitationDialog, setShowSharedInvitationDialog] = useState(false);
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(null);
  const [changedToDo, setChangedToDo] = useState(undefined);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [invitationSharingVars, setInvitationSharingVars] = useState(null);

  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));

  const handleEditButtonClick = toDo => {
    setChangedToDo(toDo);
    if (toDo.toDoType === "takeAnotherAssessment") {
      setShowAssessmentDialog(true);
    } else if (toDo.toDoType === "profileCompletion") {
      setShowUserDialog(true);
    } else if (toDo.toDoType === "openInvitation") {
      setShowSharedInvitationDialog(true);
    } else if (toDo.toDoType === "phq9Worksheet") {
      setShowAssessmentDialog(constants.assessmentIDs.phq9);
    }

    gtag("event", "partner_todo", {
      todo_type: toDo.toDoType,
    });
  };

  const handleDialogClose = buttonClicked => {
    setShowAlertDialog(false);

    if (buttonClicked === "ok") {
      if (changedToDo.toDoType === "profileCompletion") {
        userODM.omniData.isVerified = false;
        props.updatePartnerRequest(userODM);
      }
    }
    setChangedToDo(undefined);
  };

  const getSvgIconPath = toDo => {
    return toDo.isCompleted ? getCompletedLinkPath() : getClickableLinkPath();
  };

  const getEditButtonToolTip = (toDoType, isCompleted) => {
    return isCompleted
      ? "Nice Work! Completed!"
      : toDoType === "profileCompletion"
        ? "Open User Dialog"
        : toDoType === "preScreenWorksheet"
          ? "Launch Memory Questionnaire Wizard"
          : toDoType === "openInvitation"
            ? "Open Invitation Dialog"
            : "";
  };

  const getEditButtonIcon = (toDoType, isCompleted) => {
    if (isCompleted) {
      return <CheckBoxRoundedIcon sx={{ height: 24, width: 24 }} />;
    }

    if (toDoType === "medication") {
      return <CheckBoxOutlineBlankRoundedIcon sx={{ height: 24, width: 24 }} />;
    }

    if (toDoType === "profileCompletion") {
      return <EditIcon sx={{ height: 24, width: 24 }} />;
    }

    if (toDoType === "preScreenWorksheet") {
      return <LaunchRounded sx={{ height: 24, width: 24 }} />;
    }

    if (toDoType === "takeAnotherAssessment") {
      return <LaunchRounded sx={{ height: 24, width: 24 }} />;
    }
    return <LaunchMailIcon sx={{ height: 24, width: 24 }} />;
  };

  const [titleText, setTitleText] = useState("");
  const [title, setTitle] = useState("Loading...");

  const getToDoMainColor = toDo => {
    let mainColor = theme.palette.success.main;

    if (toDo.type === "profile") {
      mainColor = toDo.isCompleted ? theme.palette.success.main : theme.palette.warning.main;
    }

    return mainColor;
  };

  const populateInvitationSharingVars = org => {
    let invShareVars = constants.defaultInvitationSharingVars;

    if (org === constants.organizationTypeEnums.hyperbaric) {
      invShareVars = constants.hyperbaricInvitationSharingVars;
    }
    return invShareVars;
  };

  // Need a function to add a PHQ9 todo for every subject where the omniHealthHistory
  // depression question.answerOption = "yes"

  useEffect(() => {
    if (showWelcomMessages) {
      setTimeout(() => {
        enqueueSnackbar("Thank you!", {
          variant: "success",
        });
      }, 2000);
    }
  }, [showWelcomMessages, enqueueSnackbar]);

  useEffect(() => {
    if (userToDos) {
      setTitle("Health Coach");
      let length = userToDos.length;
      setTitleText(`${length} ${length === 1 ? "Suggestions" : "Suggestions"}`);
    }
  }, [userToDos]);

  useEffect(() => {
    if (userToDos == null && organizationODM != null && userODM != null) {
      //find!ak THIS IS REPLACED WHEN WE MOVE TODO TO THE DB/db
      loadUserToDosRequest();
      setInvitationSharingVars(
        populateInvitationSharingVars(organizationODM.omniData.organizationType),
      );
    }
  }, [organizationODM, userODM, userToDos, loadUserToDosRequest]);

  return (
    <Widget>
      <WidgetTitle title={title} text={titleText} loading={userODM == null}></WidgetTitle>

      {userToDos == null && <WidgetSkeleton />}
      {userToDos != null && (
        <WidgetList>
          {userToDos.map(toDo => {
            return (
              <ListItem
                key={toDo.key}
                disableGutters={true}
                sx={[
                  {
                    paddingRight: theme.spacing(1),
                    paddingLeft: theme.spacing(1),
                    paddingTop: 0,
                    paddingBottom: 0,
                    marginBottom: theme.spacing(1),
                    borderRadius: theme.spacing(1),
                    boxShadow: theme.shadows[3],
                    cursor: "pointer",
                    userSelect: "none",
                    webkitUserSelect: "none",
                    backgroundColor: alpha(getToDoMainColor(toDo), 0.75),
                    "& .MuiIconButton-root": {
                      transition: "all 0.15s ease-out",
                      opacity: 0,
                    },
                    "&:hover": {
                      backgroundColor: alpha(getToDoMainColor(toDo), 0.5),
                      cursor: "pointer",
                      "& .MuiIconButton-root": {
                        opacity: 1,
                      },
                      "& .MuiAvatar-root": {
                        "& .MuiSvgIcon-root": {
                          animationName: `${muiSvgIconHover}`,
                          animationDuration: ".3s",
                          animationIterationCount: "1",
                          animationTimingFunction: "ease-in-out",
                          "& path": {
                            d: `path("M8.79 9.24V5.5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v3.74c1.21-.81 2-2.18 2-3.74 0-2.49-2.01-4.5-4.5-4.5s-4.5 2.01-4.5 4.5c0 1.56.79 2.93 2 3.74zm5.5 2.47c-.28-.14-.58-.21-.89-.21h-.61v-6c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v10.74l-3.44-.72c-.37-.08-.76.04-1.03.31-.43.44-.43 1.14 0 1.58l4.01 4.01c.38.37.89.58 1.42.58h6.1c1 0 1.84-.73 1.98-1.72l.63-4.47c.12-.85-.32-1.69-1.09-2.07l-4.08-2.03z")`,
                          },
                        },
                      },
                    },
                  },
                ]}
                onClick={() => handleEditButtonClick(toDo)}
              >
                <Tooltip placement="right">
                  <OmniAvatar
                    sx={[
                      {
                        width: 36,
                        height: 36,
                        fontSize: "1.8rem",
                        backgroundColor: theme.palette.success.contrastText,
                        fill: darken(getToDoMainColor(toDo), 0.5),
                        border: `2px solid ${darken(getToDoMainColor(toDo), 0.5)}`,
                      },
                    ]}
                  >
                    <SvgIcon
                      sx={[
                        {
                          height: 30,
                          width: 30,
                          color: darken(getToDoMainColor(toDo), 0.5),
                          backgroundColor: theme.palette.success.contrastText,
                          fill: darken(getToDoMainColor(toDo), 0.5),
                          transform: `rotate(15deg)`,
                        },
                      ]}
                    >
                      {getSvgIconPath(toDo)}
                    </SvgIcon>
                  </OmniAvatar>
                </Tooltip>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: "-2px !Important",
                        marginLeft: theme.spacing(1),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: theme.palette.commonground.contrastText,
                        webkitUserSelect: "none",
                        userSelect: "none",
                      }}
                    >
                      {toDo.description}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="h6"
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        marginLeft: theme.spacing(1),
                        marginTop: `-${theme.spacing(0.75)}`,
                        marginBottom: `-${theme.spacing(0.9)}`,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: theme.palette.commonground.contrastText,
                        webkitUserSelect: "none",
                        userSelect: "none",
                        textTransform: "capitalize",
                      }}
                    >
                      {`${toDo.isCompleted ? toDo.completedText : toDo.incompleteText}`}
                    </Typography>
                  }
                />
                {!mobileLayout && (
                  <Tooltip
                    title={getEditButtonToolTip(toDo.toDoType, toDo.isCompleted)}
                    placement="left"
                  >
                    <IconButton
                      sx={[
                        {
                          position: "absolute",
                          top: theme.spacing(1),
                          right: theme.spacing(1),
                          height: 32,
                          width: 32,
                          borderRadius: "50%",
                          "&:hover": {
                            color: darken(getToDoMainColor(toDo), 0.5),
                            background: lighten(getToDoMainColor(toDo), 0.75),
                          },
                        },
                      ]}
                      onClick={() => handleEditButtonClick(toDo)}
                      size="large"
                    >
                      {getEditButtonIcon(toDo.toDoType, toDo.isCompleted)}
                    </IconButton>
                  </Tooltip>
                )}
              </ListItem>
            );
          })}
        </WidgetList>
      )}
      {showUserDialog && (
        <UserDialog
          open={showUserDialog}
          tabId={1}
          omniODM={deepCopyObject(userODM)}
          onAccept={partner => {
            // Set the isVerified Flag
            partner.omniData.isVerified = true;
            //thats not what verified is for
            props.updatePartnerRequest(partner);
            setShowUserDialog(false);
          }}
          onClose={() => {
            setShowUserDialog(false);
          }}
        />
      )}
      {showAlertDialog && (
        <AlertDialog
          dialogTitle="Change completion status"
          dialogMessage="Are you sure you want to mark this to do item as incomplete?"
          open={showAlertDialog}
          onAccept={() => {
            setShowAlertDialog(false);
            handleDialogClose("ok");
          }}
          onClose={() => {
            setShowAlertDialog(false);
            handleDialogClose("cancel");
          }}
        />
      )}
      {showSharedInvitationDialog && (
        <SharedInvitationDialog
          sharedInvitationKey={
            partnerInvites[
              partnerInvites.findIndex(invite => invite.omniData.inviteType === "shared")
            ].omniData.inviteKey
          }
          open={showSharedInvitationDialog}
          onAccept={() => {
            setShowSharedInvitationDialog(false);
            handleDialogClose("ok");
          }}
          onClose={() => {
            setShowSharedInvitationDialog(false);
            handleDialogClose("cancel");
          }}
          invitationSharingVars={invitationSharingVars}
        />
      )}
      {showAssessmentDialog != null && (
        <AssessmentWizardDialog
          open={showAssessmentDialog != null}
          // lockAssessmentOmniType={showAssessmentDialog}
          onClose={() => {
            setShowAssessmentDialog(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  patientAssessments: state.patientSession.patientAssessments,
  partnerODM: state.partnerSession.partnerODM,
  partnerPatients: state.partnerSession.partnerPatients,
  partnerAssessments: state.partnerSession.partnerAssessments,
  partnerInvites: state.partnerSession.partnerInvites,
  userODM: state.userSession.userODM,
  userToDos: state.userSession.userToDos,
  organizationODM: state.userSession.organizationODM,
  awsUserGroup: state.userSession.awsGroup,
});

const mapDispatchToProps = {
  reflectUpdatedPatientODMRequest: PartnerSession.reflectUpdatedPatientODMRequest,

  updatePartnerRequest: PartnerSession.updatePartnerRequest,
  updatePatientRequest: PatientSession.updatePatientRequest,
  updateAssessmentRequest: AssessmentSession.updateAssessmentRequest,
  updateUserToDosRequest: UserSession.updateUserToDosRequest,
  loadUserToDosRequest: UserSession.loadUserToDosRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PartnerTodo);
